import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SchoolIcon from "@mui/icons-material/School";
import WarningIcon from "@mui/icons-material/Warning";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { useClassroom } from "../../../context/ClassroomContext";
import {
  getClassroom,
  getClassAnalytics,
} from "../../../services/classroomService";
import { useHandleError } from "../../../hooks/useHandleError";
import {
  getModuleName,
  getModuleColorFromId,
  getWeekFromModuleId,
} from "../../../utils/moduleUtils";

// ============================================================
// KPI Card
// ============================================================
const KpiCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  bgTint?: string;
}> = ({ label, value, icon, color, bgTint }) => (
  <Card
    sx={{
      flex: "1 1 160px",
      minWidth: 160,
      backgroundColor: bgTint || "background.paper",
    }}
  >
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          backgroundColor: color,
          borderRadius: "50%",
          width: 44,
          height: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// ============================================================
// Mastery cell color
// ============================================================
const getMasteryColor = (score: number | null): string => {
  if (score === null) return "#f5f5f5";
  if (score >= 80) return "#c8e6c9"; // green
  if (score >= 50) return "#fff9c4"; // yellow
  return "#ffcdd2"; // red
};

// ============================================================
// Main Component
// ============================================================
const ClassroomAnalytics: React.FC = () => {
  const { classroomId } = useParams<{ classroomId: string }>();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const { classroom, setClassroom } = useClassroom();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!classroomId) return;
    if (!classroom || classroom._id !== classroomId) {
      getClassroom(classroomId)
        .then((res) => res.data && setClassroom(res.data))
        .catch(handleError);
    }
  }, [classroomId]);

  useEffect(() => {
    if (!classroomId) return;
    setLoading(true);
    getClassAnalytics(classroomId)
      .then((res) => setData(res.data))
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [classroomId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No data available.</Typography>
      </Box>
    );
  }

  const {
    classAvgScore,
    classAvgProgress,
    totalStudents,
    modulePerformance,
    weeklyEngagement,
    atRiskStudents,
    gradeDistribution,
    masteryGrid,
    masteryModuleIds,
  } = data;

  // Sort modules by week then module number
  const sortedModules = [...(modulePerformance || [])].sort(
    (a: any, b: any) => a.moduleId.localeCompare(b.moduleId)
  );

  // Prepare module bar chart data
  const moduleLabels = sortedModules.map((m: any) => getModuleName(m.moduleId));
  const moduleScores = sortedModules.map((m: any) =>
    Math.round(m.avgScore)
  );
  const moduleColors = sortedModules.map((m: any) =>
    getModuleColorFromId(m.moduleId)
  );

  // Weekly engagement chart data
  const weekLabels = (weeklyEngagement || []).map(
    (w: any) => `W${w.weekNumber}`
  );
  const weekStudents = (weeklyEngagement || []).map(
    (w: any) => w.studentsReached
  );
  const weekProgress = (weeklyEngagement || []).map((w: any) =>
    Math.round(w.avgProgress)
  );

  // Grade distribution data
  const gradeBands = (gradeDistribution || []).map((g: any) => g.band);
  const gradeCounts = (gradeDistribution || []).map((g: any) => g.count);

  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/teacher/classrooms")}
        >
          Classrooms
        </Link>
        <Typography color="text.primary">
          {classroom?.classroomName || "..."} — Analytics
        </Typography>
      </Breadcrumbs>

      <Typography variant="h5" gutterBottom>
        Class Analytics
      </Typography>

      {/* KPI Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        <KpiCard
          label="Students"
          value={totalStudents}
          icon={<PeopleIcon sx={{ color: "white" }} />}
          color="#5c6bc0"
        />
        <KpiCard
          label="Avg Score"
          value={`${Math.round(classAvgScore)}%`}
          icon={<SchoolIcon sx={{ color: "white" }} />}
          color={classAvgScore >= 70 ? "#43a047" : classAvgScore >= 50 ? "#fb8c00" : "#e53935"}
        />
        <KpiCard
          label="Avg Progress"
          value={`${Math.round(classAvgProgress)}%`}
          icon={<TrendingUpIcon sx={{ color: "white" }} />}
          color="#26a69a"
        />
        <KpiCard
          label="At Risk"
          value={atRiskStudents?.length || 0}
          icon={<WarningIcon sx={{ color: "white" }} />}
          color={atRiskStudents?.length > 0 ? "#e53935" : "#43a047"}
          bgTint={atRiskStudents?.length > 0 ? "#fff3f3" : undefined}
        />
      </Box>

      {/* Row: Grade Distribution + Module Scores */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Grade Distribution */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Grade Distribution
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Student count per average score band
            </Typography>
            {gradeCounts.some((c: number) => c > 0) ? (
              <BarChart
                xAxis={[{ scaleType: "band", data: gradeBands }]}
                series={[
                  {
                    data: gradeCounts,
                    color: "#5c6bc0",
                    label: "Students",
                  },
                ]}
                height={250}
              />
            ) : (
              <Typography color="textSecondary" sx={{ py: 4, textAlign: "center" }}>
                No quiz data yet
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Module Performance */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Score by Module
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Average score per module type
            </Typography>
            {moduleLabels.length > 0 ? (
              <BarChart
                yAxis={[{ scaleType: "band", data: moduleLabels }]}
                series={[
                  {
                    data: moduleScores,
                    label: "Avg Score %",
                  },
                ]}
                layout="horizontal"
                height={250}
                xAxis={[{ min: 0, max: 100 }]}
              />
            ) : (
              <Typography color="textSecondary" sx={{ py: 4, textAlign: "center" }}>
                No quiz data yet
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Weekly Engagement Line Chart */}
      {weekLabels.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Weekly Engagement
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Students reached and average progress over weeks
            </Typography>
            <LineChart
              xAxis={[{ scaleType: "point", data: weekLabels }]}
              series={[
                {
                  data: weekStudents,
                  label: "Students Reached",
                  color: "#5c6bc0",
                },
                {
                  data: weekProgress,
                  label: "Avg Progress %",
                  color: "#26a69a",
                },
              ]}
              height={300}
            />
          </CardContent>
        </Card>
      )}

      {/* Mastery Grid */}
      {masteryGrid?.length > 0 && masteryModuleIds?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Mastery Grid
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Per-student, per-module best score. Hover for details.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 1 }}>
                      Student
                    </TableCell>
                    {masteryModuleIds.map((mid: string) => (
                      <TableCell
                        key={mid}
                        align="center"
                        sx={{ fontWeight: "bold", fontSize: ".7rem", minWidth: 60 }}
                      >
                        {getModuleName(mid)}
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          W{getWeekFromModuleId(mid)}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {masteryGrid.map((row: any) => (
                    <TableRow
                      key={row.studentId}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(
                          `/teacher/classrooms/${classroomId}/students/${row.studentId}`
                        )
                      }
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.studentName}
                      </TableCell>
                      {masteryModuleIds.map((mid: string) => {
                        const score = row[mid];
                        return (
                          <Tooltip
                            key={mid}
                            title={
                              score !== null
                                ? `${getModuleName(mid)}: ${Math.round(score)}%`
                                : "Not attempted"
                            }
                          >
                            <TableCell
                              align="center"
                              sx={{
                                backgroundColor: getMasteryColor(score),
                                fontSize: ".75rem",
                                fontWeight: score !== null ? 600 : 400,
                                color: score === null ? "#bbb" : "inherit",
                                p: 0.5,
                              }}
                            >
                              {score !== null ? `${Math.round(score)}` : "-"}
                            </TableCell>
                          </Tooltip>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* At-Risk Students */}
      {atRiskStudents?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              At-Risk Students
            </Typography>
            {[...atRiskStudents]
              .sort((a: any, b: any) => b.reasons.length - a.reasons.length)
              .map((s: any) => (
                <Alert
                  key={s.studentId}
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{ mb: 1, cursor: "pointer" }}
                  onClick={() =>
                    navigate(
                      `/teacher/classrooms/${classroomId}/students/${s.studentId}`
                    )
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <strong>
                      {s.firstName} {s.lastName}
                    </strong>
                    {s.avgScore !== null && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(s.avgScore, 100)}
                        color={s.avgScore >= 50 ? "warning" : "error"}
                        sx={{ width: 80, height: 6, borderRadius: 3 }}
                      />
                    )}
                    {s.reasons.map((r: string, i: number) => (
                      <Chip
                        key={i}
                        label={r}
                        size="small"
                        color="warning"
                        sx={{ fontSize: ".65rem" }}
                      />
                    ))}
                  </Box>
                </Alert>
              ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ClassroomAnalytics;
